import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import dayjs from "dayjs";

import IconButton from "../../../../../../packages/IconButton";
import MoveRightIcon from "../../../../../../packages/SVGs/MoveRightIcon";
import WPButton from "../../../../../../packages/WPButton";
import WPCopyToClipboard from "../../../../../../packages/WPCopyToClipBoard/WPCopyToClipboard";
import WPHeading from "../../../../../../packages/WPHeading";
import { useToastNotification } from "../../../../../../packages/WPToastNotification";

import useTimezone from "../../../../../../hooks/useTimezone";

import LicenseDetailsPermissionList from "./LicenseDetailsPermissionList";

import { DATE_FORMATS } from "../../../../../../constants/global.constants";
import {
  LICENSE_STATUS,
  LICENSE_VIEW_USER_MODAL_KEYS,
  STATUS_FILTER_ICONS,
} from "../../../../../../constants/LicenseManagement.constants";

import {
  EditIcon,
  LicenseLineSeperatorIcon,
} from "../../../../../../assets/icons";

import RightTickMarkBigIcon from "../../../../../../common/icons/RightTickMarkBigIcon";
import CircularLoader from "../../../../../../common/styles/components/circularLoader";

import { assignUsersLicense } from "../../../../../../network/LicenseApis";

import "./LicenseDetailsCard.scss";

const LICENSE_STATUS_ICON_MAP = {
  [LICENSE_STATUS.active]: STATUS_FILTER_ICONS.active,
  [LICENSE_STATUS.expiringSoon]: STATUS_FILTER_ICONS.expiringSoon,
  [LICENSE_STATUS.expired]: STATUS_FILTER_ICONS.expired,
  [LICENSE_STATUS.terminated]: STATUS_FILTER_ICONS.terminated,
};

const LicenseDetailsCard = ({
  license,
  onLicenseAction,
  userData,
  onLicenseUpdate,
}) => {
  const { formatTime } = useTimezone();
  const { t } = useTranslation();
  const { showErrorNotification } = useToastNotification();
  const [isCardFlipped, setCardFlipped] = useState(false);
  const [isEditingKeyManager, setIsEditingKeyManager] = useState(false);
  const [keyAccountManager, setKeyAccountManager] = useState("");
  const [isSavingKeyManager, setIsSavingKeyManager] = useState(false);
  const isSaveInFlightRef = useRef(false);

  const {
    id: licenseId,
    name: licenseName,
    assignedDate,
    expiryDate,
    licenseType,
    licenseApprover,
    licenseStatus,
  } = license || {};

  const licensePermissions =
    useSelector((state) => state.licensePermissions) || [];

  const headerLicenseName = [licenseType, licenseName]
    .filter(Boolean)
    .join(" ");

  const handleFlipCard = useCallback(() => {
    setCardFlipped((prev) => !prev);
  }, []);

  const handleEditKeyAccountManager = useCallback(() => {
    setIsEditingKeyManager(true);
    setKeyAccountManager(licenseApprover || "");
  }, [licenseApprover]);

  const handleKeyManagerChange = useCallback((event) => {
    setKeyAccountManager(event.target.value);
  }, []);

  const handleCancelKeyManagerEdit = useCallback(() => {
    setIsEditingKeyManager(false);
    setKeyAccountManager(licenseApprover || "");
  }, [licenseApprover]);

  const handleSaveKeyAccountManager = useCallback(async () => {
    if (isSaveInFlightRef.current || isSavingKeyManager) {
      return;
    }

    const trimmedValue = keyAccountManager.trim();

    if (trimmedValue === (licenseApprover || "")) {
      setIsEditingKeyManager(false);
      return;
    }

    const formattedExpiryDate = dayjs(expiryDate).isValid()
      ? dayjs(expiryDate).format(DATE_FORMATS.YYYY_MM_DD)
      : "";

    const payload = [
      {
        userId: userData?.userId,
        email: userData?.email,
        licenses: [
          {
            id: licenseId,
            name: licenseName,
            assignedDate,
            expiryDate: formattedExpiryDate,
            licenseType,
            licenseApprover: trimmedValue,
          },
        ],
      },
    ];

    isSaveInFlightRef.current = true;
    setIsEditingKeyManager(false);
    setIsSavingKeyManager(true);
    try {
      const res = await assignUsersLicense(payload);
      if (res?.status !== 200) {
        throw new Error("Failed to update key account manager");
      }

      onLicenseUpdate?.(userData?.userId, {
        ...license,
        licenseApprover: trimmedValue,
      });
    } catch (error) {
      setKeyAccountManager(licenseApprover || "");
      showErrorNotification(t("licenseManagement.licenseAssignmentFailed"));
    } finally {
      isSaveInFlightRef.current = false;
      setIsSavingKeyManager(false);
    }
  }, [
    keyAccountManager,
    licenseApprover,
    userData,
    licenseId,
    licenseName,
    assignedDate,
    expiryDate,
    licenseType,
    onLicenseUpdate,
    license,
    showErrorNotification,
    t,
    isSavingKeyManager,
    isSaveInFlightRef,
  ]);

  const handleKeyManagerKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSaveKeyAccountManager();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        handleCancelKeyManagerEdit();
      }
    },
    [handleSaveKeyAccountManager, handleCancelKeyManagerEdit],
  );

  const LicenseStatusTag = ({ status }) => {
    const icon = LICENSE_STATUS_ICON_MAP[status] || null;
    if (!status) return null;

    return (
      <div
        className={`license-card-status-tag-custom status-${status}`}
        id="license_status_tag"
      >
        {icon}
        <WPHeading
          className="license-card-status-text"
          text={status}
          secondary
          size="xSmall"
          type="default"
        />
      </div>
    );
  };

  const LicenseDateBlock = ({
    label,
    value,
    labelColor = "var(--du-color-grey-700)",
    valueColor = "var(--du-color-grey)",
  }) => (
    <div className="license-card-date-block">
      <WPHeading
        text={label}
        size="xxSmall"
        secondary
        color={labelColor}
        fontNoto
      />
      <WPHeading
        text={value}
        size="xxSmall"
        secondary
        color={valueColor}
        fontNoto
      />
    </div>
  );

  const licenseDates = useMemo(
    () => ({
      assigned: formatTime(assignedDate, true, DATE_FORMATS.MMM_DD_YYYY),
      expiry: formatTime(expiryDate, true, DATE_FORMATS.MMM_DD_YYYY),
    }),
    [assignedDate, expiryDate, formatTime],
  );

  const isTerminatedDisabled =
    licenseStatus === LICENSE_STATUS.expired ||
    licenseStatus === LICENSE_STATUS.terminated;
  const isEditDisabled =
    licenseStatus === LICENSE_STATUS.expired ||
    licenseStatus === LICENSE_STATUS.terminated;
  const editTooltip =
    licenseStatus === LICENSE_STATUS.expired
      ? t("licenseManagement.unableToEditExpired")
      : licenseStatus === LICENSE_STATUS.terminated
        ? t("licenseManagement.unableToEditTerminated")
        : t("ValueStrings.edit");

  return (
    <div className="license-details-card">
      <div className="license-card-shell">
        <div className="license-card-header-row">
          <div className="license-card-header-left">
            <WPHeading
              text={headerLicenseName}
              size="small"
              className="license-card-title"
              type="default"
              tooltip={headerLicenseName}
              wrapContent
            />
            <div
              className="license-card-flip"
              id="license_card_flip"
              onClick={handleFlipCard}
            >
              <WPHeading
                text={
                  isCardFlipped
                    ? t("licenseManagement.details")
                    : t("licenseManagement.permissions")
                }
                size="xxSmall"
                color="var(--du-color-grey)"
                secondary
                fontNoto
                className="license-card-flip-text"
              />
              <MoveRightIcon className="license-card-flip-icon" disabled />
            </div>
          </div>
          <LicenseStatusTag status={licenseStatus} />
        </div>

        {isSavingKeyManager && (
          <div className="license-card-saving-overlay">
            <CircularLoader size="24px" />
          </div>
        )}

        <div className="license-card-body">
          {!isCardFlipped ? (
            <>
              <div className="license-card-dates-row">
                <LicenseDateBlock
                  label={t("licenseManagement.licenseAssignedOn")}
                  value={licenseDates.assigned}
                />
                <div className="license-card-separator">
                  <LicenseLineSeperatorIcon />
                </div>
                <LicenseDateBlock
                  label={t("licenseManagement.licenseExpiryDate")}
                  value={licenseDates.expiry}
                />
              </div>
              <div className="license-card-manager-box">
                <div className="license-card-manager-mail">
                  <WPHeading
                    text={t("licenseManagement.keyAccountManager")}
                    size="xxSmall"
                    fontNoto
                    secondary
                    color="var(--du-color-grey)"
                    className="license-card-manager-label"
                  />
                  {isEditingKeyManager ? (
                    <div className="license-card-manager-editable">
                      <input
                        type="text"
                        value={keyAccountManager}
                        onChange={handleKeyManagerChange}
                        onKeyDown={handleKeyManagerKeyDown}
                        onBlur={handleSaveKeyAccountManager}
                        className="license-card-manager-input"
                        maxLength={50}
                        autoFocus
                        disabled={isSavingKeyManager}
                      />
                      <IconButton
                        id={`save-kam-${licenseId}`}
                        name="save-key-acc-manager-btn"
                        ariaLabel="save"
                        onClick={handleSaveKeyAccountManager}
                        className="license-card-manager-save-btn"
                        tooltip={t("save")}
                        disabled={isSavingKeyManager}
                      >
                        <RightTickMarkBigIcon
                          className="license-card-manager-save-icon"
                          fillColor="var(--du-color-black_70)"
                        />
                      </IconButton>
                    </div>
                  ) : (
                    <div className="license-card-manager-display">
                      <WPHeading
                        text={licenseApprover || "NA"}
                        className="license-card-manager-email"
                        size="xxSmall"
                        fontNoto
                        secondary
                        type="default"
                        tooltip={licenseApprover}
                      />
                      <IconButton
                        id={`edit-kam-${licenseId}`}
                        name="edit-key-acc-manager-btn"
                        ariaLabel="edit"
                        onClick={handleEditKeyAccountManager}
                        className="license-card-manager-edit-btn"
                        classNameTooltip="license-edit-disabled-tooltip"
                        tooltip={editTooltip}
                        disabled={isEditDisabled}
                        isDisabledTooltip={isEditDisabled}
                      >
                        <EditIcon
                          className={`license-card-manager-edit-icon${
                            isEditDisabled ? " is-disabled" : ""
                          }`}
                        />
                      </IconButton>
                    </div>
                  )}
                </div>
                <WPCopyToClipboard
                  text={licenseApprover || ""}
                  id="license-card-copy-icon"
                  disabled={!licenseApprover}
                />
              </div>
            </>
          ) : (
            <div className="license-card-back-content">
              <LicenseDetailsPermissionList
                licenseId={licenseId}
                licensePermissions={licensePermissions}
              />
            </div>
          )}
        </div>

        <div className="license-card-footer">
          <WPButton
            variant="text"
            type="primary"
            size="small"
            disabled={isTerminatedDisabled}
            customClass="license-terminate-btn"
            id={`terminate-${licenseId}`}
            onClick={() =>
              onLicenseAction?.(
                LICENSE_VIEW_USER_MODAL_KEYS.terminateModal,
                licenseId,
              )
            }
          >
            {t("licenseManagement.terminateButton")}
          </WPButton>
          <WPButton
            variant="outlined"
            type="primary"
            size="small"
            id={`renew-${licenseId}`}
            onClick={() =>
              onLicenseAction?.(
                LICENSE_VIEW_USER_MODAL_KEYS.renewModal,
                licenseId,
              )
            }
          >
            {t("licenseManagement.renewButton")}
          </WPButton>
        </div>
      </div>
    </div>
  );
};

export default LicenseDetailsCard;
